import React from 'react'
import { cn } from '../../utils/cn'

export interface LoadingProps {
  /**
   * Loading variant
   */
  variant?: 'spinner' | 'overlay' | 'skeleton'

  /**
   * Size (for spinner)
   */
  size?: 'sm' | 'md' | 'lg' | 'xl'

  /**
   * Text to display below spinner/in overlay
   */
  text?: string

  /**
   * Skeleton width (for skeleton variant)
   */
  width?: string

  /**
   * Skeleton height (for skeleton variant)
   */
  height?: string

  /**
   * Custom className
   */
  className?: string
}

const spinnerSizes = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-2',
  lg: 'h-16 w-16 border-4',
  xl: 'h-24 w-24 border-4',
}

export const Loading: React.FC<LoadingProps> = ({
  variant = 'spinner',
  size = 'md',
  text,
  width,
  height,
  className,
}) => {
  if (variant === 'spinner') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
        <div
          className={cn(
            'animate-spin rounded-full border-toast-gold border-t-transparent',
            spinnerSizes[size]
          )}
          role="status"
          aria-label="Loading"
        />
        {text && <p className="text-sm text-deep-charcoal/60">{text}</p>}
      </div>
    )
  }

  if (variant === 'overlay') {
    return (
      <div
        className={cn(
          'fixed inset-0 z-50',
          'bg-gradient-to-br from-butter-cream/95 to-warm-cream/95',
          'flex items-center justify-center',
          className
        )}
      >
        <div className="text-center max-w-md px-6">
          <div className={cn('animate-spin rounded-full border-toast-gold border-t-transparent mx-auto mb-6', spinnerSizes.lg)} />
          {text && <p className="text-lg text-deep-charcoal">{text}</p>}
        </div>
      </div>
    )
  }

  if (variant === 'skeleton') {
    return (
      <div
        className={cn('animate-pulse bg-soft-tan/30 rounded', className)}
        style={{ width: width || '100%', height: height || '1rem' }}
      />
    )
  }

  return null
}

Loading.displayName = 'Loading'

export default Loading
